import { useCallback, useMemo } from 'react';
import filter from 'lodash/filter';
import first from 'lodash/first';
import concat from 'lodash/concat';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import pick from 'lodash/pick';
import compact from 'lodash/compact';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import uniq from 'lodash/uniq';
import map from 'lodash/map';

import {
  ChangeProductsFiltersFunc,
  FetchProductsFilterFields,
  FetchProductsFilters
} from '../../../../../../../../products/productsTypes';
import { IdFilterType } from '../../../../../../../../../types';
import {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../../../categories/categoriesTypes';
import { FetchProductCategoriesQueryResponse } from '../../../../../../../../products/queries/fetchProductCategories.query';

import { useCacheProductsAggregations } from '../../../../../../../../products/hooks/useCacheProductsAggregations';
import { useThreeDStockCategories } from '../../../../../../../hooks/useThreeDStockCategories';

import { getChangeProductsFiltersCategoriesData } from '../../../../../../../../products/utils/getChangeProductsFiltersCategoriesData';

import { getAllParentsCategories } from '../../../../../../../../categories/utils/getAllParentsCategories';

import { StockTabs } from '../../../../../../../stockConstants';

interface ProductsFilterCategoriesOptions {
  productsFilters: FetchProductsFilters;
  defaultProductCategoryId?: IdFilterType;
  changeProductsFilters: ChangeProductsFiltersFunc;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  changeCategory: ChangeProductsFiltersFunc;
}

function useProductsFilterCategories({
  productsFilters,
  defaultProductCategoryId,
  changeProductsFilters,
  changeCategory,
  checkedCategories,
  setCheckedCategories
}: ProductsFilterCategoriesOptions) {
  const checkedCategoryIds = map(checkedCategories, 'id');

  const productsFiltersForCategories = useMemo<FetchProductsFilters>(() => {
    const filtersWithoutCategories = pick(productsFilters, [
      FetchProductsFilterFields.TEAM_IDS,
      FetchProductsFilterFields.CLIENT_IDS,
      FetchProductsFilterFields.NDA,
      FetchProductsFilterFields.FAVORITE,
      FetchProductsFilterFields.BLOCKED,
      FetchProductsFilterFields.CURRENT_TEAM_FAVORITE,
      FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS,
      FetchProductsFilterFields.NAME_SKU_TAGS,
      FetchProductsFilterFields.SIMILAR_IMAGE
    ]);

    return {
      ...filtersWithoutCategories,
      productCategoryId: defaultProductCategoryId
    };
  }, [defaultProductCategoryId, productsFilters]);

  const { productsAggregations, productsAggregationsFetched } =
    useCacheProductsAggregations({
      productsFilters: productsFiltersForCategories
    });

  const {
    categories: productCategoriesWithParents,
    categoriesFetched: allProductCategoriesFetched,
    currentCategory
  } = useThreeDStockCategories({
    tab: StockTabs.PRODUCTS,
    categoryIds: defaultProductCategoryId?.in
  });

  const productAggregationsCategories = useMemo<
    FetchProductCategoriesQueryResponse[]
  >(() => {
    if (isEmpty(productsAggregations)) {
      return productCategoriesWithParents;
    }

    const availableCategories = concat(
      productsAggregations?.categories,
      productsAggregations?.parent_categories
    );

    return productCategoriesWithParents?.filter((category) =>
      availableCategories?.some(
        (availableCategory) => availableCategory.id === category.id
      )
    );
  }, [productCategoriesWithParents, productsAggregations]);

  const listCategories = useMemo<FetchProductCategoriesQueryResponse[]>(() => {
    const defaultCurrentCategoryId = first(defaultProductCategoryId?.in);

    return getAllParentsCategories(
      productCategoriesWithParents,
      productAggregationsCategories,
      defaultCurrentCategoryId
    )?.sort((a, b) => {
      return (
        trim(a.localizedName)?.localeCompare(trim(b.localizedName), undefined, {
          numeric: true,
          sensitivity: 'base'
        }) || 0
      );
    });
  }, [
    defaultProductCategoryId?.in,
    productAggregationsCategories,
    productCategoriesWithParents
  ]);

  const handleCheckedCategory = useCallback<(categoryId: CategoryID) => void>(
    (categoryId) => {
      const isChecked = checkedCategoryIds?.includes(categoryId);

      const newCheckedCategoryIds = isChecked
        ? checkedCategoryIds.filter((id) => id !== categoryId)
        : [...checkedCategoryIds, categoryId];

      const newCheckedCategories = filter(
        productCategoriesWithParents,
        (category) => newCheckedCategoryIds.includes(category.id)
      );

      setCheckedCategories(newCheckedCategories);

      if (isEmpty(newCheckedCategoryIds)) {
        const { changeFilters, resetFilters } =
          getChangeProductsFiltersCategoriesData({
            productCategory: currentCategory,
            categories: productCategoriesWithParents
          });

        return changeProductsFilters(changeFilters, resetFilters);
      }

      const combinedFilters = reduce(
        newCheckedCategoryIds,
        (result, categoryId) => {
          const category = find(productCategoriesWithParents, [
            'id',
            categoryId
          ]);

          const { changeFilters } = getChangeProductsFiltersCategoriesData({
            productCategory: category,
            categories: productCategoriesWithParents
          });

          return {
            [FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS]: uniq(
              compact(
                concat(
                  result[FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS],
                  changeFilters[
                    FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS
                  ]
                )
              )
            ),
            [FetchProductsFilterFields.PRODUCT_CATEGORY_ID]: {
              in: uniq(
                compact(
                  concat(
                    result[FetchProductsFilterFields.PRODUCT_CATEGORY_ID]?.in,
                    changeFilters[FetchProductsFilterFields.PRODUCT_CATEGORY_ID]
                      ?.in
                  )
                )
              )
            }
          };
        },
        {
          [FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS]: [],
          [FetchProductsFilterFields.PRODUCT_CATEGORY_ID]: { in: [] }
        }
      );

      const resetFilters = compact([
        !isEmpty(
          combinedFilters[FetchProductsFilterFields.PRODUCT_CATEGORY_ID]?.in
        )
          ? null
          : FetchProductsFilterFields.PRODUCT_CATEGORY_ID,
        !isEmpty(
          combinedFilters[FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS]
        )
          ? null
          : FetchProductsFilterFields.PRODUCT_PARENT_CATEGORY_IDS
      ]);

      changeProductsFilters(combinedFilters, resetFilters);
    },
    [
      changeProductsFilters,
      checkedCategoryIds,
      currentCategory,
      productCategoriesWithParents,
      setCheckedCategories
    ]
  );

  const handleChangeCategory = useCallback<(categoryId: CategoryID) => void>(
    (categoryId) => {
      const productCategory = find(productCategoriesWithParents, [
        'id',
        categoryId
      ]);

      const { changeFilters, resetFilters } =
        getChangeProductsFiltersCategoriesData({
          productCategory,
          categories: productCategoriesWithParents
        });

      changeCategory(changeFilters, resetFilters);
    },
    [changeCategory, productCategoriesWithParents]
  );

  return {
    listCategories,
    listCategoriesFetched:
      productsAggregationsFetched && allProductCategoriesFetched,
    checkedCategoryIds,
    handleCheckedCategory,
    handleChangeCategory
  };
}

export default useProductsFilterCategories;
